import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, toArray } from 'rxjs/operators';
import { Networks } from 'src/app/modules/shared/models/networks.enum';
import { PostMessageService } from 'src/app/modules/shared/services/post-message.service';
import formatHolidayCategory from '../../../../../helpers/format-holiday-category';
import fixArray from '../../../../../utils/fixArray';
import { MappingItem } from '../../../../shared/interfaces/mapping-item';
import { EnvironmentService } from '../../../../shared/services/environment.service';
import { ProjectService } from '../../../../shared/services/project.service';
import { HolidayAccomodation } from '../interfaces/holiday-accomodation';
import { HolidayCard } from '../interfaces/holiday-card';
import { HolidaySearchParams } from '../interfaces/holiday-search-params';
import { HolidaySolution } from '../interfaces/holiday-solution';
import { HolidayTour } from '../interfaces/holiday-tour';
import { TourOperators } from 'src/app/enums/Tops.enum';
import { TourSeachParams } from '../../../tour/tour-search-page/interfaces/itour-search-params';

@Injectable({
  providedIn: 'root'
})
export class HolidaySearchService {

  constructor(private _http: HttpClient,
              private _environmentService: EnvironmentService,
              private _postMessageService: PostMessageService,
              private _projectService: ProjectService) {
  }

  private setHolidayCustomProperty(holiday: any): boolean {
    return holiday.IsCustom || (holiday.IsNetworkHoliday && TourOperators.otaId === holiday.TourOperatorID)
  }

  httpCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  formatAccomodation(accomodation: any): HolidayAccomodation {
    return {
      category: accomodation.Accomodation_Cat ?
        formatHolidayCategory(accomodation.Accomodation_Cat, accomodation.Accomodation_Type) : undefined,
      name: accomodation.Name,
      id: accomodation.Id,
      image: accomodation.Photo,
      parent: accomodation.Parent
    };
  }

  formatSolutionBasis(basis: any): MappingItem {
    //  Normalizza trattamento All Inclusive (che per alcuni prodotti viene restituito come "AI")
    if(basis.Code === "AI") basis.Code = "All Inclusive";
    return {
      id: basis.Code || basis, name: basis.Name
    };
  }

  formatSolution(solution: any): HolidaySolution {
    const basis = solution.Basis_Collection ? fixArray(solution.Basis_Collection.Basis)[0] :
      solution.HolidayBasis_Collection ? fixArray(solution.HolidayBasis_Collection.HolidayBasis)[0] : undefined;
    return {
      airport: solution.DeparturePlaceID < 3000000 ? solution.DeparturePlaceID : undefined,
      basis: basis ? this.formatSolutionBasis(basis) : undefined,
      dateFrom: dayjs(solution.DepartureDate).toDate(),
      dateTo: dayjs(solution.DepartureDate).add(solution.Nights, 'day').toDate(),
      days: solution.Days,
      id: solution.Id,
      nights: solution.Nights,
      price: parseFloat(solution.MKPriceCP),
      totalPrice: parseFloat(solution.TotalPrice),
      source: solution.Source,
      hasNetQuote: !!solution.isThereNetQuote,
      hasTotalPrice: !!solution.TotalPrice,
      operative: solution?.Operatives_Collection?.Operative ?? {},
      operativeString: this.formatOperativeString(solution?.Operatives_Collection?.Operative),
      packet: solution?.Packet?.id

    };
  }

  formatOperativeString(operative): string {
    if (operative?.OperativeDepartureStations) {
      const tmpArray = [];
      operative.OperativeDepartureStations.forEach(station => {
        if (tmpArray.length < 4) {
          tmpArray.indexOf(station.Destination.name) === -1 ? tmpArray.push(station.Destination.name) : null;
        }
      });

      return tmpArray.reduce((acc, val, idx) => {
        acc += `${idx === 0 ? '' : ', '}${val}`;
        if (idx === tmpArray.length - 1) {
          acc += '...';
        }
        return acc;
      }, '');
    } else {
      return '';
    }
  }

  formatSolutions(solutions: any[]): HolidaySolution[] {
    return solutions.map(e => this.formatSolution(e));
  }

  formatTour(tour: any): HolidayTour {
    return {
      ...tour,
      id: tour.id,
      image: tour.coverImageBaseUrl + tour.coverImage,
      stops: this.formatStops(tour?.stops),
      destinations: tour.destinations.join(', ')
    };
  }

  formatStops(stops: any[]): string[] {
    if(!stops) return [];
    return stops.sort((a, b) => a.day - b.day).map(stop => stop.destinationName);
  }

  formatHoliday(holiday: any): HolidayCard {
    return {
      accomodation: holiday.Accomodations_Collection.AccomodationSlim ?
        this.formatAccomodation(fixArray(holiday.Accomodations_Collection.AccomodationSlim)[0]) : undefined,
      tour: holiday.Tour ? this.formatTour(holiday.Tour) : undefined,
      destination: fixArray(holiday.Places_Collection)[0],
      filteredSolutions: [],
      bestSolution: undefined,
      logo: holiday.Logo,
      id: holiday.Id,
      title: holiday?.Tour ? holiday?.Tour.title : holiday.Title,
      isLive: holiday.IsLive,
      isCustom: this.setHolidayCustomProperty(holiday),
      pluginSource: holiday.Plugin_Source,
      solutions: this.formatSolutions(fixArray(holiday.Solutions_Collection.SolutionSlim)),
      source: holiday.Source,
      realTourOperatorId: holiday.RealTourOperatorID,
      tourOperator: holiday.TourOperatorID,
      tourOperatorLogo: `https://cdn.otosrl.com/images/touroperator/mini/${holiday.TourOperatorID}-${holiday.TourOperatorID}.png`,
      type: holiday.Type
    };
  }

  formatHolidays(holidays: any[]): Observable<HolidayCard[]> {
    return from(holidays).pipe(
      map(e => this.formatHoliday(e)),
      toArray()
    );
  }

  getFormattedHolidays(searchParams: HolidaySearchParams | TourSeachParams): Observable<HolidayCard[]> {
    return this.getHolidays(searchParams).pipe(
      mergeMap(e => this.formatHolidays(e)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getFormattedLiveHolidays(searchParams: HolidaySearchParams): Observable<HolidayCard[]> {
    return this.getLiveHolidays(searchParams).pipe(
      mergeMap(e => this.formatHolidays(e)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getLiveHolidays(searchParams: HolidaySearchParams): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
      this._projectService.getAgencyNetwork(),
      this._postMessageService.isB2B(),
      this._projectService.hasOnlyNetQuotes()
    ]).pipe(
      mergeMap(e => {
        const hasOnlyNetQuotes = e[4];
        searchParams.channel = 'B2C';
        if (Number(e[2].id_network) && Number(e[2].id_network) === Networks.WTG) {
          searchParams.onlyNetQuotes = hasOnlyNetQuotes;
          searchParams.onlyCustomHoliday = false;
          searchParams.quoteTypology = 'Both';
        }
        const liveUrl = `${e[0].searchUrl}/index.php/holiday/${e[1]}/searchLive/slim`;
        const liveCacheString = liveUrl + JSON.stringify(searchParams);
        if (!this.httpCache.get(liveCacheString)) {
          this.httpCache.set(liveCacheString, this._http.post<any>(liveUrl, searchParams).pipe(shareReplay({
            bufferSize: 1,
            refCount: true
          })));
        }
        return this.httpCache.get(liveCacheString);
      }),
      map(e => [
        ...fixArray(e?.Result?.Holiday_Collection?.HolidaySlim),
      ].filter(i => i)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getHolidays(searchParams: HolidaySearchParams | TourSeachParams): Observable<any[]> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
      this._projectService.getAgencyNetwork(),
      this._projectService.hasOnlyNetQuotes()
    ]).pipe(
      mergeMap(e => {
        const hasOnlyNetQuotes = e[3];
        searchParams.channel = 'B2C';
        if (Number(e[2].id_network) && Number(e[2].id_network) === Networks.WTG) {
          searchParams.onlyNetQuotes = hasOnlyNetQuotes;
          searchParams.onlyCustomHoliday = false;
          searchParams.quoteTypology = 'Both';
        }
        searchParams.onlyCustomHoliday = false;
        const liveUrl = `${e[0].searchUrl}/index.php/holiday/${e[1]}/searchLive/slim`;
        const liveCacheString = liveUrl + JSON.stringify(searchParams);
        if (!this.httpCache.get(liveCacheString)) {
          this.httpCache.set(liveCacheString, this._http.post<any>(liveUrl, searchParams).pipe(shareReplay({
            bufferSize: 1,
            refCount: true
          })));
        }
        const batchUrl = `${e[0].searchUrl}/index.php/holiday/${e[1]}/search/slim`;
        const batchCacheString = batchUrl + JSON.stringify(searchParams);
        if (!this.httpCache.get(batchCacheString)) {
          this.httpCache.set(batchCacheString, this._http.post<any>(batchUrl, searchParams).pipe(shareReplay({
            bufferSize: 1,
            refCount: true
          })));
        }
        return forkJoin([
          this.httpCache.get(liveCacheString),
          searchParams.onlyCustomHoliday ? of({Result: {Holiday_Collection: {HolidaySlim: []}}}) : this.httpCache.get(batchCacheString),
        ]);
      }),
      map(e => [
        ...fixArray(e[0]?.Result?.Holiday_Collection?.HolidaySlim),
        ...fixArray(e[1]?.Result?.Holiday_Collection?.HolidaySlim)
      ].filter(i => i)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }
}
